import React, { useEffect } from 'react';

const Replication = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Database Replication in Distributed Systems - Complete Guide 2024';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master database replication strategies for fault tolerance and scalability. Learn master-slave, master-master, and synchronous vs asynchronous replication patterns.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master database replication strategies for fault tolerance and scalability. Learn master-slave, master-master, and synchronous vs asynchronous replication patterns.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Database Replication in Distributed Systems</h1>
        <p style={styles.subtitle}>
          Ensure fault tolerance and scalability with data copies across multiple nodes
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>What is Database Replication?</h2>
          <p style={styles.paragraph}>
            Database replication is the process of creating and maintaining multiple copies of data across different 
            database servers or locations. This technique provides fault tolerance, improves read performance, and 
            enables geographic distribution of data for better user experience.
          </p>
          <p style={styles.paragraph}>
            Replication ensures that if one database server fails, other replicas can continue serving requests, 
            providing high availability and disaster recovery capabilities. It also allows read operations to be 
            distributed across multiple servers, reducing load on the primary database.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Types of Database Replication</h2>
          
          <h3 style={styles.heading}>Master-Slave Replication</h3>
          <p style={styles.paragraph}>
            In master-slave replication, one database server acts as the master (primary) that handles all write 
            operations, while one or more slave servers (replicas) receive copies of the data and handle read operations.
          </p>
          <pre style={styles.codeBlock}>
{`Master-Slave Architecture:

    Writes     ┌─────────────┐
    ---------> │   Master    │
               │ (Primary)   │
               └──────┬──────┘
                      │ Replication
                      ▼
               ┌─────────────┐    ┌─────────────┐
    Reads <--- │   Slave 1   │    │   Slave 2   │
               │ (Replica)   │    │ (Replica)   │
               └─────────────┘    └─────────────┘

Advantages:
✓ Simple to implement and understand
✓ Scales read operations effectively
✓ Clear separation of read and write workloads

Disadvantages:
✗ Single point of failure for writes
✗ Potential for read lag (eventual consistency)
✗ Master can become a bottleneck`}
          </pre>

          <h3 style={styles.heading}>Master-Master Replication</h3>
          <p style={styles.paragraph}>
            Master-master replication allows multiple database servers to accept both read and write operations. 
            Changes made to any master are replicated to all other masters, providing better write scalability 
            but introducing complexity in conflict resolution.
          </p>
          <pre style={styles.codeBlock}>
{`Master-Master Architecture:

    ┌─────────────┐ <-- Replication --> ┌─────────────┐
    │  Master 1   │                     │  Master 2   │
    │             │                     │             │
    └──────┬──────┘                     └──────┬──────┘
           │                                   │
           ▼                                   ▼
    Reads & Writes                     Reads & Writes

Advantages:
✓ No single point of failure
✓ Better write scalability
✓ Geographic distribution of writes

Disadvantages:
✗ Complex conflict resolution
✗ Potential for data inconsistency
✗ More complex to implement and maintain`}
          </pre>

          <h3 style={styles.heading}>Chain Replication</h3>
          <p style={styles.paragraph}>
            Chain replication organizes replicas in a linear chain where writes flow from head to tail, and reads 
            are served from the tail. This provides strong consistency while maintaining good performance.
          </p>
          <pre style={styles.codeBlock}>
{`Chain Replication:

    Writes     ┌─────────┐      ┌─────────┐      ┌─────────┐     Reads
    ---------> │  Head   │ ---> │ Middle  │ ---> │  Tail   │ <---------
               │(Master) │      │(Replica)│      │(Replica)│
               └─────────┘      └─────────┘      └─────────┘

Advantages:
✓ Strong consistency for reads
✓ Good fault tolerance
✓ Simple failure recovery

Disadvantages:
✗ Higher write latency
✗ Chain can become bottleneck
✗ Complex reconfiguration on failures`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Synchronous vs Asynchronous Replication</h2>
          
          <h3 style={styles.heading}>Synchronous Replication</h3>
          <p style={styles.paragraph}>
            Synchronous replication ensures that data is written to all replicas before acknowledging the write 
            operation to the client. This provides strong consistency but higher latency.
          </p>
          <pre style={styles.codeBlock}>
{`Synchronous Replication Process:

1. Client sends write request to master
2. Master writes data locally
3. Master sends data to all replicas
4. Replicas write data and send acknowledgment
5. Master waits for ALL replica acknowledgments
6. Master acknowledges write to client

Pros:
✓ Strong consistency across all replicas
✓ No data loss on master failure
✓ Immediate consistency for reads

Cons:
✗ Higher write latency
✗ Reduced availability (any replica failure affects writes)
✗ Network partitions can block writes`}
          </pre>

          <h3 style={styles.heading}>Asynchronous Replication</h3>
          <p style={styles.paragraph}>
            Asynchronous replication acknowledges writes to the client immediately after writing to the master, 
            then replicates data to other servers in the background. This provides better performance but 
            eventual consistency.
          </p>
          <pre style={styles.codeBlock}>
{`Asynchronous Replication Process:

1. Client sends write request to master
2. Master writes data locally
3. Master immediately acknowledges write to client
4. Master asynchronously replicates to replicas
5. Replicas eventually receive and apply changes

Pros:
✓ Lower write latency
✓ Better availability (replica failures don't affect writes)
✓ Higher throughput

Cons:
✗ Potential data loss on master failure
✗ Read replicas may serve stale data
✗ Eventual consistency only`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Implementation Examples</h2>
          
          <h3 style={styles.heading}>MySQL Master-Slave Setup</h3>
          <p style={styles.paragraph}>
            Here's how to configure MySQL master-slave replication for high availability and read scaling:
          </p>
          <pre style={styles.codeBlock}>
{`# Master Configuration (my.cnf)
[mysqld]
server-id = 1
log-bin = mysql-bin
binlog-format = ROW
binlog-do-db = myapp_db

# Create replication user
CREATE USER 'replication'@'%' IDENTIFIED BY 'password';
GRANT REPLICATION SLAVE ON *.* TO 'replication'@'%';
FLUSH PRIVILEGES;

# Get master status
SHOW MASTER STATUS;
# Note the File and Position values

# Slave Configuration (my.cnf)
[mysqld]
server-id = 2
relay-log = mysql-relay-bin
log-slave-updates = 1
read-only = 1

# Configure slave
CHANGE MASTER TO
  MASTER_HOST='master-server-ip',
  MASTER_USER='replication',
  MASTER_PASSWORD='password',
  MASTER_LOG_FILE='mysql-bin.000001',
  MASTER_LOG_POS=154;

START SLAVE;
SHOW SLAVE STATUS\G`}
          </pre>

          <h3 style={styles.heading}>Application-Level Read/Write Splitting</h3>
          <p style={styles.paragraph}>
            Implement read/write splitting in your application to route writes to master and reads to replicas:
          </p>
          <pre style={styles.codeBlock}>
{`class DatabaseRouter:
    def __init__(self, master_config, replica_configs):
        self.master = DatabaseConnection(master_config)
        self.replicas = [DatabaseConnection(config) for config in replica_configs]
        self.replica_index = 0
    
    def execute_write(self, query, params=None):
        """Execute write operations on master"""
        return self.master.execute(query, params)
    
    def execute_read(self, query, params=None, use_master=False):
        """Execute read operations on replica (or master if specified)"""
        if use_master:
            return self.master.execute(query, params)
        
        # Round-robin load balancing across replicas
        replica = self.replicas[self.replica_index]
        self.replica_index = (self.replica_index + 1) % len(self.replicas)
        
        try:
            return replica.execute(query, params)
        except ConnectionError:
            # Fallback to master if replica is unavailable
            return self.master.execute(query, params)
    
    def get_user(self, user_id):
        """Read operation - use replica"""
        return self.execute_read(
            "SELECT * FROM users WHERE id = %s", [user_id]
        )
    
    def create_user(self, user_data):
        """Write operation - use master"""
        return self.execute_write(
            "INSERT INTO users (name, email) VALUES (%s, %s)",
            [user_data['name'], user_data['email']]
        )
    
    def get_user_after_update(self, user_id):
        """Read after write - use master to ensure consistency"""
        return self.execute_read(
            "SELECT * FROM users WHERE id = %s", [user_id], use_master=True
        )`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Replication Lag and Consistency</h2>
          
          <h3 style={styles.heading}>Understanding Replication Lag</h3>
          <p style={styles.paragraph}>
            Replication lag is the time delay between when data is written to the master and when it appears on 
            replicas. This lag can cause consistency issues where recent writes are not immediately visible on replicas.
          </p>
          <pre style={styles.codeBlock}>
{`Replication Lag Scenarios:

Time: T0 - User updates profile on Master
Time: T1 - User immediately views profile from Replica
Result: Old profile data (replication lag)

Solutions:
1. Read from master after writes (read-your-writes consistency)
2. Use session affinity to same replica
3. Implement lag monitoring and alerts
4. Use synchronous replication for critical data`}
          </pre>

          <h3 style={styles.heading}>Monitoring Replication Health</h3>
          <p style={styles.paragraph}>
            Monitor replication lag and health to ensure your replicas are keeping up with the master:
          </p>
          <pre style={styles.codeBlock}>
{`# MySQL Replication Monitoring
SHOW SLAVE STATUS\G

Key Metrics:
- Slave_IO_Running: Should be 'Yes'
- Slave_SQL_Running: Should be 'Yes'
- Seconds_Behind_Master: Replication lag in seconds
- Last_Error: Any replication errors

# Python monitoring script
def check_replication_health(replica_connection):
    result = replica_connection.execute("SHOW SLAVE STATUS")
    
    if not result:
        return {"status": "error", "message": "Not a slave"}
    
    status = result[0]
    
    return {
        "io_running": status['Slave_IO_Running'] == 'Yes',
        "sql_running": status['Slave_SQL_Running'] == 'Yes',
        "lag_seconds": status['Seconds_Behind_Master'],
        "last_error": status['Last_Error']
    }`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Failover and Recovery</h2>
          
          <h3 style={styles.heading}>Automatic Failover</h3>
          <p style={styles.paragraph}>
            Implement automatic failover to promote a replica to master when the primary fails:
          </p>
          <pre style={styles.codeBlock}>
{`class ReplicationManager:
    def __init__(self, master_config, replica_configs):
        self.master_config = master_config
        self.replica_configs = replica_configs
        self.current_master = None
        self.replicas = []
        self.setup_connections()
    
    def monitor_master_health(self):
        """Continuously monitor master health"""
        while True:
            try:
                self.current_master.ping()
                time.sleep(5)  # Check every 5 seconds
            except ConnectionError:
                self.handle_master_failure()
    
    def handle_master_failure(self):
        """Handle master failure by promoting a replica"""
        # 1. Stop accepting writes
        self.write_enabled = False
        
        # 2. Find the most up-to-date replica
        best_replica = self.find_most_current_replica()
        
        # 3. Promote replica to master
        self.promote_replica_to_master(best_replica)
        
        # 4. Reconfigure other replicas
        self.reconfigure_replicas(best_replica)
        
        # 5. Resume writes
        self.write_enabled = True
    
    def find_most_current_replica(self):
        """Find replica with least lag"""
        best_replica = None
        min_lag = float('inf')
        
        for replica in self.replicas:
            try:
                status = replica.execute("SHOW SLAVE STATUS")[0]
                lag = status.get('Seconds_Behind_Master', float('inf'))
                
                if lag < min_lag:
                    min_lag = lag
                    best_replica = replica
            except:
                continue
        
        return best_replica
    
    def promote_replica_to_master(self, replica):
        """Promote replica to become new master"""
        # Stop slave processes
        replica.execute("STOP SLAVE")
        
        # Reset slave configuration
        replica.execute("RESET SLAVE ALL")
        
        # Enable writes on new master
        replica.execute("SET GLOBAL read_only = OFF")
        
        # Update current master reference
        self.current_master = replica`}
          </pre>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Examples</h2>
          
          <h3 style={styles.heading}>Netflix's Cassandra Replication</h3>
          <p style={styles.paragraph}>
            Netflix uses Cassandra with replication factor of 3 across multiple availability zones. They use 
            asynchronous replication with eventual consistency, prioritizing availability over strong consistency 
            for their streaming service.
          </p>

          <h3 style={styles.heading}>GitHub's MySQL Setup</h3>
          <p style={styles.paragraph}>
            GitHub uses MySQL with master-slave replication across multiple data centers. They implement automatic 
            failover using orchestrator and maintain read replicas for scaling read operations across their platform.
          </p>

          <h3 style={styles.heading}>Amazon RDS Multi-AZ</h3>
          <p style={styles.paragraph}>
            Amazon RDS provides synchronous replication across availability zones for high availability. The standby 
            replica automatically takes over if the primary fails, providing minimal downtime for applications.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Best Practices</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>
              <strong>Choose the right replication type:</strong> Use synchronous for critical data requiring 
              consistency, asynchronous for better performance and availability.
            </li>
            <li style={styles.listItem}>
              <strong>Monitor replication lag:</strong> Set up alerts for high replication lag that could indicate 
              performance issues or failures.
            </li>
            <li style={styles.listItem}>
              <strong>Plan for failover:</strong> Test failover procedures regularly and automate the process 
              where possible to minimize downtime.
            </li>
            <li style={styles.listItem}>
              <strong>Implement read/write splitting:</strong> Route reads to replicas and writes to master to 
              distribute load effectively.
            </li>
            <li style={styles.listItem}>
              <strong>Use consistent replica selection:</strong> Implement session affinity or read-your-writes 
              consistency to avoid confusing users with stale data.
            </li>
            <li style={styles.listItem}>
              <strong>Regular backup verification:</strong> Test that your replicas can be used for disaster 
              recovery by regularly performing restore tests.
            </li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Key Takeaways</h2>
          <p style={styles.paragraph}>
            Database replication is essential for building fault-tolerant and scalable systems. The choice between 
            synchronous and asynchronous replication, and between different replication topologies, depends on your 
            specific requirements for consistency, availability, and performance.
          </p>
          <p style={styles.paragraph}>
            Remember that replication introduces complexity in terms of consistency management, failover procedures, 
            and monitoring. Plan your replication strategy carefully, test failover scenarios regularly, and monitor 
            replication health continuously to ensure your system can handle failures gracefully.
          </p>
        </section>
      </main>
    </article>
  );
};

export default Replication;
